/**
 * Centralized date formatting utilities.
 */

/**
 * Get the user's timezone string (e.g., "America/New_York")
 */
export const getUserTimezone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Format a date string from the local database (UTC without timezone info).
 * Adds 'Z' suffix to treat as UTC, then converts to user's local timezone.
 *
 * @param dateString - UTC date string from the database (e.g., "2024-12-15T10:30:00")
 * @returns Formatted date string (e.g., "12/15/2024 3:45 PM") or 'N/A' if invalid
 */
export const formatDate = (dateString: string | undefined | null): string => {
  if (!dateString) return 'N/A';

  try {
    // Database dates are UTC but may not have 'Z' suffix - add it if missing
    // Only add 'Z' if there's no timezone indicator already
    const hasTimezone = dateString.endsWith('Z') || /[+-]\d{2}:?\d{2}$/.test(dateString);
    const normalizedDate = hasTimezone ? dateString : dateString + 'Z';
    const date = new Date(normalizedDate);

    if (isNaN(date.getTime())) return 'N/A';

    return date.toLocaleString('en-US', {
      dateStyle: 'short',
      timeStyle: 'short',
      hour12: true,
    });
  } catch {
    return 'N/A';
  }
};

/**
 * Format a UTC date string with explicit timezone display.
 *
 * @param dateString - UTC date string from the backend
 * @returns Object with formatted date and timezone string
 */
export const formatDateWithTimezone = (dateString: string | undefined | null): { date: string; timezone: string } => {
  return {
    date: formatDate(dateString),
    timezone: getUserTimezone(),
  };
};
